import React, { useState } from 'react';
import {
    Bell,
    Mail,
    Smartphone,
    CheckCircle,
    AlertCircle,
    MessageCircle,
    Calendar,
    Clock
} from 'lucide-react';

const NotificationDemo = () => {
    // 模擬通知數據
    const [notificationData, setNotificationData] = useState({
        unreadCount: 7,
        channels: [
            {
                id: 'email',
                name: 'Email',
                icon: <Mail />,
                unreadCount: 3,
                notifications: [
                    {
                        id: 1,
                        title: '季度績效報告已準備就緒',
                        sender: '人力資源部',
                        time: '今天 09:30',
                        status: 'unread'
                    },
                    {
                        id: 2,
                        title: '年度培訓課程報名通知',
                        sender: '企業培訓中心',
                        time: '昨天 14:45',
                        status: 'read'
                    }
                ]
            },
            {
                id: 'app',
                name: 'App推播',
                icon: <Smartphone />,
                unreadCount: 2,
                notifications: [
                    {
                        id: 1,
                        title: '專案進度會議提醒',
                        sender: '專案管理部',
                        time: '今天 13:15',
                        status: 'unread'
                    },
                    {
                        id: 2,
                        title: '加班申請已審核',
                        sender: '系統通知',
                        time: '昨天 17:20',
                        status: 'read'
                    }
                ]
            },
            {
                id: 'internal',
                name: '系統訊息',
                icon: <MessageCircle />,
                unreadCount: 2,
                notifications: [
                    {
                        id: 1,
                        title: '薪資系統更新提醒',
                        sender: '財務部',
                        time: '今天 10:45',
                        status: 'unread'
                    },
                    {
                        id: 2,
                        title: '公司福利政策調整',
                        sender: '人力資源部',
                        time: '昨天 16:30',
                        status: 'read'
                    }
                ]
            }
        ],
        upcomingEvents: [
            {
                id: 1,
                title: '季度績效檢討會議',
                date: '2024-03-25',
                time: '14:00',
                type: 'meeting'
            },
            {
                id: 2,
                title: '新人培訓課程',
                date: '2024-03-30',
                time: '09:30',
                type: 'training'
            }
        ]
    });

    // 通知類型圖示
    const getNotificationIcon = (type) => {
        switch (type) {
            case 'meeting':
                return <Calendar className="text-blue-500" />;
            case 'training':
                return <Bell className="text-green-500" />;
            default:
                return <Clock className="text-gray-500" />;
        }
    };

    // 標記通知為已讀
    const markNotificationAsRead = (channelId, notificationId) => {
        const updatedChannels = notificationData.channels.map(channel => {
            if (channel.id === channelId) {
                return {
                    ...channel,
                    notifications: channel.notifications.map(notification =>
                        notification.id === notificationId
                            ? { ...notification, status: 'read' }
                            : notification
                    ),
                    unreadCount: channel.unreadCount > 0 ? channel.unreadCount - 1 : 0
                };
            }
            return channel;
        });

        setNotificationData(prev => ({
            ...prev,
            unreadCount: Math.max(0, prev.unreadCount - 1),
            channels: updatedChannels
        }));
    };

    return (
        <div className="p-8">
            <div className="grid md:grid-cols-2 gap-8">
                {/* 左側：通知渠道 */}
                <div>
                    <h2 className="text-2xl font-bold mb-4 flex items-center">
                        <Bell className="mr-2 text-primary-600" />
                        通知與提醒中心
                    </h2>

                    <div className="bg-gray-50 p-6 rounded-lg mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-semibold flex items-center">
                                <AlertCircle className="mr-2 text-yellow-500" />
                                未讀通知總數
                            </h3>
                            <span className="bg-primary-600 text-white px-3 py-1 rounded-full">
                                {notificationData.unreadCount}
                            </span>
                        </div>

                        {notificationData.channels.map((channel) => (
                            <div
                                key={channel.id}
                                className="bg-white p-4 rounded-lg shadow-sm mb-4"
                            >
                                <div className="flex justify-between items-center mb-3">
                                    <div className="flex items-center">
                                        {React.cloneElement(channel.icon, {
                                            className: "mr-2 text-primary-600"
                                        })}
                                        <span className="font-semibold">{channel.name}</span>
                                    </div>
                                    {channel.unreadCount > 0 && (
                                        <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                                            {channel.unreadCount}
                                        </span>
                                    )}
                                </div>

                                {channel.notifications.map((notification) => (
                                    <div
                                        key={notification.id}
                                        className={`p-3 rounded-lg mb-2 flex justify-between items-center ${notification.status === 'unread'
                                                ? 'bg-primary-50 border-2 border-primary-100'
                                                : 'bg-gray-50'
                                            }`}
                                    >
                                        <div>
                                            <div className="font-medium">{notification.title}</div>
                                            <div className="text-sm text-gray-600">
                                                來自：{notification.sender}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">
                                                {notification.time}
                                            </div>
                                        </div>
                                        {notification.status === 'unread' && (
                                            <button
                                                onClick={() => markNotificationAsRead(channel.id, notification.id)}
                                                className="text-primary-600 hover:bg-primary-100 p-2 rounded-full"
                                            >
                                                <CheckCircle size={20} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>

                {/* 右側：近期事件提醒 */}
                <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center">
                        <Calendar className="mr-2 text-primary-600" />
                        近期事件提醒
                    </h3>

                    <div className="bg-white rounded-lg shadow-md">
                        <div className="p-4 border-b bg-gray-50">
                            <h4 className="font-semibold">即將到來的活動</h4>
                        </div>
                        {notificationData.upcomingEvents.map((event) => (
                            <div
                                key={event.id}
                                className="p-4 border-b flex items-center justify-between hover:bg-gray-50"
                            >
                                <div className="flex items-center">
                                    {getNotificationIcon(event.type)}
                                    <div className="ml-3">
                                        <div className="font-medium">{event.title}</div>
                                        <div className="text-sm text-gray-600">
                                            {event.date} {event.time}
                                        </div>
                                    </div>
                                </div>
                                <button className="text-primary-600 hover:bg-primary-50 p-2 rounded-full">
                                    <MessageCircle size={16} />
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold mb-3 flex items-center">
                            <AlertCircle className="mr-2 text-yellow-500" />
                            通知設定
                        </h4>
                        <p className="text-sm text-gray-600">
                            您可以自定義通知偏好設定，選擇接收的通知類型和渠道。
                            確保重要訊息不會被忽略，同時避免過多不必要的通知。
                        </p>
                        <button className="mt-3 w-full bg-primary-600 text-white p-3 rounded-lg hover:bg-primary-700 transition-colors">
                            管理通知設定
                        </button>
                    </div>
                </div>
            </div>

            {/* 底部操作區域 */}
            <div className="mt-8 flex justify-between items-center bg-white p-6 rounded-lg shadow-md">
                <div>
                    <h4 className="font-semibold">智能通知管理</h4>
                    <p className="text-sm text-gray-600">
                        整合多渠道通知，提供即時、精準的企業溝通解決方案
                    </p>
                </div>
                <div className="flex space-x-4">
                    <button className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors">
                        通知中心
                    </button>
                    <button className="border border-primary-600 text-primary-600 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors">
                        訊息設定
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NotificationDemo;